/**
 * Influencer platforms page — feature #118.
 *
 * Operator-facing authoring surface for multi-platform influencer attribution.
 * Four cards, top to bottom:
 *   1. Link form — attach a platform (Instagram, YouTube, TikTok, Twitter,
 *      Twitch, Facebook) to an influencer with a handle + optional profile URL.
 *   2. Links table — lists the existing platform links for the selected
 *      influencer, with a delete action.
 *   3. Attribution tester — POSTs a click or conversion attribution to the
 *      API so operators can smoke the end-to-end funnel.
 *   4. Attribution report — GET /attribution/:id?from&to and renders
 *      per-platform counts as a stacked bar with a total.
 *
 * The page is dep-free beyond the shared client + TanStack Query. All form
 * parsing lives in `./api.ts` behind a `safeParse` surface.
 */

import { useEffect, useMemo, useState } from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	ATTRIBUTION_KINDS,
	type AttributionCounts,
	type AttributionKind,
	attributionFormSchema,
	type AttributionReport,
	type AttributionWindow,
	buildBarSegments,
	createPlatformLink,
	dateInputToUnix,
	deletePlatformLink,
	fetchAttributionReport,
	fetchLinksByInfluencer,
	influencerPlatformsQueryKeys,
	linkFormSchema,
	type PlatformLinkRow,
	type Platform,
	PLATFORM_LABELS,
	PLATFORMS,
	recordAttribution,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — match os-targeting page. Light theme, zinc borders, 6-8px
// rounded, emerald focus ring, brand-gradient only on the page headline.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const PRIMARY_BUTTON =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const SECONDARY_BUTTON =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

// Per-platform accent colours for the stacked bar. Light-theme safe; zero
// overlap with competitor branding.
const PLATFORM_COLOR: Record<Platform, string> = {
	instagram: "bg-emerald-500",
	youtube: "bg-sky-500",
	tiktok: "bg-zinc-800",
	twitter: "bg-emerald-400",
	twitch: "bg-sky-700",
	facebook: "bg-sky-400",
};

// ---------------------------------------------------------------------------
// Props — the route passes an optional `initialInfluencerId` via search.
// ---------------------------------------------------------------------------

export interface InfluencerPlatformsPageProps {
	initialInfluencerId?: string;
}

export function InfluencerPlatformsPage(
	props: InfluencerPlatformsPageProps = {},
) {
	const [selectedInfluencerId, setSelectedInfluencerId] = useState<string>(
		props.initialInfluencerId?.trim() ?? "",
	);

	const trimmedSelected = selectedInfluencerId.trim();
	const canQueryLinks = trimmedSelected.length > 0;

	return (
		<main className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-16">
			<Header />

			<LinkForm
				onLinked={(row) => {
					setSelectedInfluencerId(row.influencer_id);
				}}
			/>

			<SelectedInfluencerField
				value={selectedInfluencerId}
				onChange={setSelectedInfluencerId}
			/>

			<LinksTable
				influencerId={trimmedSelected}
				enabled={canQueryLinks}
			/>

			<AttributionTester
				defaultInfluencerId={trimmedSelected}
				onAttributed={(influencerId) => {
					setSelectedInfluencerId(influencerId);
				}}
			/>

			<AttributionView influencerId={trimmedSelected} />

			<DocHint />
		</main>
	);
}

export default InfluencerPlatformsPage;

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function Header() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Influencers
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Multi-platform attribution
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Link each influencer to the channels they publish on, then attribute
				clicks and conversions per platform. The report card rolls everything
				up into a stacked bar so you can see the revenue mix at a glance.
			</p>
		</header>
	);
}

function DocHint() {
	return (
		<aside className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
			<p className="font-medium text-zinc-800">Field rules</p>
			<ul className="mt-2 flex list-inside list-disc flex-col gap-1">
				<li>
					Handles accept letters, digits, dashes, dots, or underscores. A
					leading &ldquo;@&rdquo; is stripped automatically.
				</li>
				<li>
					Profile URLs must use http or https. Other protocols are rejected
					before the request leaves the browser.
				</li>
				<li>
					Revenue is stored in cents — use whole numbers only. Leave blank for
					click-only events.
				</li>
			</ul>
		</aside>
	);
}

// ---------------------------------------------------------------------------
// Link form — creates / upserts a platform link.
// ---------------------------------------------------------------------------

interface LinkFormState {
	influencer_id: string;
	platform: Platform;
	handle: string;
	profile_url: string;
}

const EMPTY_LINK_FORM: LinkFormState = {
	influencer_id: "",
	platform: "instagram",
	handle: "",
	profile_url: "",
};

function LinkForm(props: { onLinked?: (row: PlatformLinkRow) => void }) {
	const queryClient = useQueryClient();
	const [form, setForm] = useState<LinkFormState>(EMPTY_LINK_FORM);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const mutation = useMutation({
		mutationFn: createPlatformLink,
		onSuccess: (row) => {
			toast.success(
				`Linked ${PLATFORM_LABELS[row.platform]} @${row.handle} to ${row.influencer_id}.`,
			);
			queryClient.invalidateQueries({
				queryKey: influencerPlatformsQueryKeys.linksByInfluencer(
					row.influencer_id,
				),
			});
			setForm((prev) => ({ ...EMPTY_LINK_FORM, platform: prev.platform }));
			setErrors({});
			props.onLinked?.(row);
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to save platform link.");
		},
	});

	function updateField<K extends keyof LinkFormState>(
		key: K,
		value: LinkFormState[K],
	) {
		setForm((prev) => ({ ...prev, [key]: value }));
		if (errors[key]) {
			setErrors((prev) => {
				const { [key]: _drop, ...rest } = prev;
				return rest;
			});
		}
	}

	function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const parsed = linkFormSchema.safeParse({
			influencer_id: form.influencer_id,
			platform: form.platform,
			handle: form.handle,
			profile_url: form.profile_url,
		});
		if (!parsed.success) {
			setErrors(parsed.errors);
			toast.error("Please fix the highlighted fields.");
			return;
		}
		setErrors({});
		mutation.mutate({
			influencer_id: parsed.data.influencer_id,
			platform: parsed.data.platform,
			handle: parsed.data.handle,
			profile_url: parsed.data.profile_url,
		});
	}

	return (
		<form
			onSubmit={handleSubmit}
			className={`${CARD} flex flex-col gap-5 p-6`}
			noValidate
		>
			<SectionHeading
				eyebrow="Step 1"
				title="Link a platform"
				description="Attach a channel to an influencer. Re-submitting with the same influencer + platform updates the handle in place."
			/>

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
				<Field
					label="Influencer ID"
					htmlFor="ipl-influencer-id"
					error={errors.influencer_id}
					hint="Tenant-scoped identifier for the influencer."
				>
					<input
						id="ipl-influencer-id"
						name="influencer_id"
						type="text"
						autoComplete="off"
						placeholder="e.g. inf_14ab9f"
						className={INPUT}
						value={form.influencer_id}
						onChange={(e) =>
							updateField("influencer_id", e.currentTarget.value)
						}
						aria-invalid={Boolean(errors.influencer_id)}
					/>
				</Field>

				<Field
					label="Handle"
					htmlFor="ipl-handle"
					error={errors.handle}
					hint="Leading @ is stripped automatically."
				>
					<input
						id="ipl-handle"
						name="handle"
						type="text"
						autoComplete="off"
						placeholder="e.g. @nis or nis"
						className={INPUT}
						value={form.handle}
						onChange={(e) => updateField("handle", e.currentTarget.value)}
						aria-invalid={Boolean(errors.handle)}
					/>
				</Field>
			</div>

			<PlatformRadioGroup
				name="link-platform"
				value={form.platform}
				onChange={(p) => updateField("platform", p)}
				error={errors.platform}
			/>

			<Field
				label="Profile URL"
				htmlFor="ipl-profile-url"
				error={errors.profile_url}
				hint="Optional. Must be http or https if provided."
			>
				<input
					id="ipl-profile-url"
					name="profile_url"
					type="url"
					autoComplete="off"
					placeholder="https://instagram.com/nis"
					className={INPUT}
					value={form.profile_url}
					onChange={(e) => updateField("profile_url", e.currentTarget.value)}
					aria-invalid={Boolean(errors.profile_url)}
				/>
			</Field>

			<div className="flex items-center justify-end gap-2 border-t border-zinc-200 pt-4">
				<button
					type="button"
					onClick={() => {
						setForm(EMPTY_LINK_FORM);
						setErrors({});
					}}
					className={SECONDARY_BUTTON}
					disabled={mutation.isPending}
				>
					Reset
				</button>
				<button
					type="submit"
					disabled={mutation.isPending}
					className={PRIMARY_BUTTON}
				>
					{mutation.isPending ? "Saving." : "Save link"}
				</button>
			</div>
		</form>
	);
}

// ---------------------------------------------------------------------------
// Selected influencer field — drives the links table + report card.
// ---------------------------------------------------------------------------

function SelectedInfluencerField(props: {
	value: string;
	onChange: (value: string) => void;
}) {
	return (
		<div className={`${CARD} flex flex-col gap-3 p-5`}>
			<SectionHeading
				eyebrow="Context"
				title="Active influencer"
				description="Set once — the table and report below reload automatically."
			/>
			<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
				<label htmlFor="ipl-active-influencer" className="sr-only">
					Active influencer ID
				</label>
				<input
					id="ipl-active-influencer"
					type="text"
					autoComplete="off"
					placeholder="inf_14ab9f"
					className={INPUT}
					value={props.value}
					onChange={(e) => props.onChange(e.currentTarget.value)}
				/>
				<button
					type="button"
					onClick={() => props.onChange("")}
					className={SECONDARY_BUTTON}
					disabled={props.value.length === 0}
				>
					Clear
				</button>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Links table — GET /by-influencer/:id
// ---------------------------------------------------------------------------

function LinksTable(props: { influencerId: string; enabled: boolean }) {
	const queryClient = useQueryClient();

	const query = useQuery({
		queryKey: influencerPlatformsQueryKeys.linksByInfluencer(
			props.influencerId,
		),
		queryFn: () => fetchLinksByInfluencer(props.influencerId),
		enabled: props.enabled,
	});

	const deleteMutation = useMutation({
		mutationFn: deletePlatformLink,
		onSuccess: () => {
			toast.success("Platform link removed.");
			queryClient.invalidateQueries({
				queryKey: influencerPlatformsQueryKeys.linksByInfluencer(
					props.influencerId,
				),
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to remove link.");
		},
	});

	const links = query.data ?? [];

	return (
		<section className={`${CARD} flex flex-col gap-4 p-6`}>
			<SectionHeading
				eyebrow="Step 2"
				title="Linked platforms"
				description={
					props.enabled
						? `Showing links for ${props.influencerId}.`
						: "Set an active influencer above to list links."
				}
			/>

			{!props.enabled ? (
				<EmptyState text="Enter an influencer ID in the context card." />
			) : query.isLoading ? (
				<EmptyState text="Loading links." />
			) : query.isError ? (
				<ErrorState
					message={
						query.error instanceof Error
							? query.error.message
							: "Unable to load links."
					}
					onRetry={() => query.refetch()}
				/>
			) : links.length === 0 ? (
				<EmptyState text="No platforms linked yet for this influencer." />
			) : (
				<div className="overflow-hidden rounded-[6px] border border-zinc-200">
					<table className="w-full border-collapse text-left text-sm">
						<thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
							<tr>
								<Th>Platform</Th>
								<Th>Handle</Th>
								<Th>Profile URL</Th>
								<Th className="w-24 text-right">Actions</Th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-200 bg-white">
							{links.map((link) => (
								<tr key={link.id} className="hover:bg-zinc-50/50">
									<Td>
										<PlatformChip platform={link.platform} />
									</Td>
									<Td>
										<span className="font-medium text-zinc-900">
											@{link.handle}
										</span>
									</Td>
									<Td>
										{link.profile_url ? (
											<a
												className="text-emerald-700 hover:text-emerald-800 hover:underline"
												href={link.profile_url}
												rel="noopener noreferrer"
												target="_blank"
											>
												{truncate(link.profile_url, 48)}
											</a>
										) : (
											<span className="text-zinc-400">—</span>
										)}
									</Td>
									<Td className="text-right">
										<button
											type="button"
											onClick={() => deleteMutation.mutate(link.id)}
											disabled={deleteMutation.isPending}
											className="rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-60"
										>
											Remove
										</button>
									</Td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}

// ---------------------------------------------------------------------------
// Attribution tester — POST /attribute
// ---------------------------------------------------------------------------

interface AttributionFormState {
	influencer_id: string;
	platform: Platform;
	kind: AttributionKind;
	revenue_cents: string;
	campaign_id: string;
}

function emptyAttributionForm(seed: string): AttributionFormState {
	return {
		influencer_id: seed,
		platform: "instagram",
		kind: "click",
		revenue_cents: "",
		campaign_id: "",
	};
}

function AttributionTester(props: {
	defaultInfluencerId: string;
	onAttributed: (influencerId: string) => void;
}) {
	const queryClient = useQueryClient();
	const [form, setForm] = useState<AttributionFormState>(() =>
		emptyAttributionForm(props.defaultInfluencerId),
	);
	const [errors, setErrors] = useState<Record<string, string>>({});

	useEffect(() => {
		setForm((prev) =>
			prev.influencer_id.trim().length === 0 && props.defaultInfluencerId
				? { ...prev, influencer_id: props.defaultInfluencerId }
				: prev,
		);
	}, [props.defaultInfluencerId]);

	const mutation = useMutation({
		mutationFn: recordAttribution,
		onSuccess: (row) => {
			toast.success(
				`Recorded ${PLATFORM_LABELS[row.platform]} attribution (id ${row.id}).`,
			);
			queryClient.invalidateQueries({
				queryKey: influencerPlatformsQueryKeys.all,
			});
			props.onAttributed(row.influencer_id);
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to record attribution.");
		},
	});

	function updateField<K extends keyof AttributionFormState>(
		key: K,
		value: AttributionFormState[K],
	) {
		setForm((prev) => ({ ...prev, [key]: value }));
		if (errors[key]) {
			setErrors((prev) => {
				const { [key]: _drop, ...rest } = prev;
				return rest;
			});
		}
	}

	function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const parsed = attributionFormSchema.safeParse({
			influencer_id: form.influencer_id,
			platform: form.platform,
			kind: form.kind,
			revenue_cents: form.revenue_cents,
			campaign_id: form.campaign_id,
		});
		if (!parsed.success) {
			setErrors(parsed.errors);
			toast.error("Please fix the highlighted fields.");
			return;
		}
		setErrors({});
		mutation.mutate({
			influencer_id: parsed.data.influencer_id,
			platform: parsed.data.platform,
			kind: parsed.data.kind,
			revenue_cents: parsed.data.revenue_cents ?? undefined,
			campaign_id: parsed.data.campaign_id,
		});
	}

	return (
		<form
			onSubmit={handleSubmit}
			className={`${CARD} flex flex-col gap-5 p-6`}
			noValidate
		>
			<SectionHeading
				eyebrow="Step 3"
				title="Attribution tester"
				description="Fire a synthetic click or conversion against /attribute to confirm the pipeline is live."
			/>

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
				<Field
					label="Influencer ID"
					htmlFor="ipl-attr-influencer"
					error={errors.influencer_id}
				>
					<input
						id="ipl-attr-influencer"
						name="influencer_id"
						type="text"
						autoComplete="off"
						className={INPUT}
						placeholder="inf_14ab9f"
						value={form.influencer_id}
						onChange={(e) =>
							updateField("influencer_id", e.currentTarget.value)
						}
						aria-invalid={Boolean(errors.influencer_id)}
					/>
				</Field>

				<Field
					label="Campaign ID"
					htmlFor="ipl-attr-campaign"
					error={errors.campaign_id}
					hint="Optional. Defaults to &ldquo;default&rdquo; when blank."
				>
					<input
						id="ipl-attr-campaign"
						name="campaign_id"
						type="text"
						autoComplete="off"
						className={INPUT}
						placeholder="camp_14ab9f"
						value={form.campaign_id}
						onChange={(e) =>
							updateField("campaign_id", e.currentTarget.value)
						}
						aria-invalid={Boolean(errors.campaign_id)}
					/>
				</Field>
			</div>

			<PlatformRadioGroup
				name="attr-platform"
				value={form.platform}
				onChange={(p) => updateField("platform", p)}
				error={errors.platform}
			/>

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
				<fieldset className="flex flex-col gap-2">
					<legend className={LABEL}>Kind</legend>
					<div
						role="radiogroup"
						aria-label="Attribution kind"
						className="inline-flex rounded-[6px] border border-zinc-200 bg-zinc-50 p-0.5"
					>
						{ATTRIBUTION_KINDS.map((kind) => (
							<button
								key={kind}
								type="button"
								role="radio"
								aria-checked={form.kind === kind}
								onClick={() => updateField("kind", kind)}
								className={`rounded-[6px] px-3 py-1.5 text-sm font-medium transition-colors ${
									form.kind === kind
										? "bg-white text-zinc-900 shadow-[0_1px_2px_0_rgb(24_24_27/0.06)]"
										: "text-zinc-600 hover:text-zinc-900"
								}`}
							>
								{kind === "click" ? "Click" : "Conversion"}
							</button>
						))}
					</div>
					{errors.kind && <p className={ERROR_TEXT}>{errors.kind}</p>}
				</fieldset>

				<Field
					label="Revenue (cents)"
					htmlFor="ipl-attr-revenue"
					error={errors.revenue_cents}
					hint="Only for conversions. Whole numbers, no decimals."
				>
					<input
						id="ipl-attr-revenue"
						name="revenue_cents"
						type="text"
						inputMode="numeric"
						autoComplete="off"
						className={INPUT}
						placeholder="1999"
						value={form.revenue_cents}
						onChange={(e) =>
							updateField("revenue_cents", e.currentTarget.value)
						}
						aria-invalid={Boolean(errors.revenue_cents)}
						disabled={form.kind === "click"}
					/>
				</Field>
			</div>

			<div className="flex items-center justify-end gap-2 border-t border-zinc-200 pt-4">
				<button
					type="button"
					onClick={() => {
						setForm(emptyAttributionForm(props.defaultInfluencerId));
						setErrors({});
					}}
					className={SECONDARY_BUTTON}
					disabled={mutation.isPending}
				>
					Reset
				</button>
				<button
					type="submit"
					disabled={mutation.isPending}
					className={PRIMARY_BUTTON}
				>
					{mutation.isPending ? "Sending." : "Record attribution"}
				</button>
			</div>
		</form>
	);
}

// ---------------------------------------------------------------------------
// Attribution view — GET /attribution/:id?from&to, stacked bar.
// ---------------------------------------------------------------------------

interface ReportFilter {
	from: string;
	to: string;
}

const EMPTY_REPORT_FILTER: ReportFilter = { from: "", to: "" };

function AttributionView(props: { influencerId: string }) {
	const [filter, setFilter] = useState<ReportFilter>(EMPTY_REPORT_FILTER);
	const [appliedWindow, setAppliedWindow] = useState<AttributionWindow>({});

	const enabled = props.influencerId.length > 0;

	const query = useQuery<AttributionReport>({
		queryKey: influencerPlatformsQueryKeys.attribution(
			props.influencerId,
			appliedWindow,
		),
		queryFn: () => fetchAttributionReport(props.influencerId, appliedWindow),
		enabled,
	});

	function applyFilter(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const from = dateInputToUnix(filter.from, false);
		const to = dateInputToUnix(filter.to, true);
		if (filter.from && from === null) {
			toast.error("From date is invalid.");
			return;
		}
		if (filter.to && to === null) {
			toast.error("To date is invalid.");
			return;
		}
		if (from !== null && to !== null && from > to) {
			toast.error("From date must be on or before To date.");
			return;
		}
		setAppliedWindow({ from, to });
	}

	function clearFilter() {
		setFilter(EMPTY_REPORT_FILTER);
		setAppliedWindow({});
	}

	return (
		<section className={`${CARD} flex flex-col gap-5 p-6`}>
			<SectionHeading
				eyebrow="Step 4"
				title="Attribution report"
				description={
					enabled
						? `Per-platform counts for ${props.influencerId}.`
						: "Pick an active influencer to load a report."
				}
			/>

			<form
				onSubmit={applyFilter}
				className="flex flex-col gap-3 sm:flex-row sm:items-end"
			>
				<div className="flex flex-1 flex-col gap-1.5">
					<label htmlFor="ipl-report-from" className={LABEL}>
						From
					</label>
					<input
						id="ipl-report-from"
						name="from"
						type="date"
						className={INPUT}
						value={filter.from}
						onChange={(e) =>
							setFilter((prev) => ({ ...prev, from: e.currentTarget.value }))
						}
					/>
				</div>
				<div className="flex flex-1 flex-col gap-1.5">
					<label htmlFor="ipl-report-to" className={LABEL}>
						To
					</label>
					<input
						id="ipl-report-to"
						name="to"
						type="date"
						className={INPUT}
						value={filter.to}
						onChange={(e) =>
							setFilter((prev) => ({ ...prev, to: e.currentTarget.value }))
						}
					/>
				</div>
				<div className="flex items-center gap-2">
					<button
						type="submit"
						className={PRIMARY_BUTTON}
						disabled={!enabled}
					>
						Apply
					</button>
					<button
						type="button"
						onClick={clearFilter}
						className={SECONDARY_BUTTON}
						disabled={
							!enabled &&
							filter.from === "" &&
							filter.to === "" &&
							appliedWindow.from == null &&
							appliedWindow.to == null
						}
					>
						Clear
					</button>
				</div>
			</form>

			{!enabled ? (
				<EmptyState text="Enter an influencer ID in the context card to load attribution." />
			) : query.isLoading ? (
				<EmptyState text="Loading report." />
			) : query.isError ? (
				<ErrorState
					message={
						query.error instanceof Error
							? query.error.message
							: "Unable to load attribution."
					}
					onRetry={() => query.refetch()}
				/>
			) : query.data ? (
				<ReportBody report={query.data} />
			) : null}
		</section>
	);
}

function ReportBody(props: { report: AttributionReport }) {
	const segments = useMemo(
		() => buildBarSegments(props.report.counts),
		[props.report.counts],
	);
	const total = props.report.total;

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-wrap items-baseline gap-3">
				<p className="text-sm text-zinc-500">Total attributions</p>
				<p className="text-3xl font-semibold tracking-tight text-zinc-900">
					{total.toLocaleString()}
				</p>
			</div>

			<StackedBar counts={props.report.counts} />

			<div className="overflow-hidden rounded-[6px] border border-zinc-200">
				<table className="w-full border-collapse text-left text-sm">
					<thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
						<tr>
							<Th>Platform</Th>
							<Th className="w-24 text-right">Count</Th>
							<Th className="w-24 text-right">Share</Th>
						</tr>
					</thead>
					<tbody className="divide-y divide-zinc-200 bg-white">
						{segments.map((seg) => (
							<tr key={seg.platform}>
								<Td>
									<PlatformChip platform={seg.platform} />
								</Td>
								<Td className="text-right font-medium text-zinc-900">
									{seg.count.toLocaleString()}
								</Td>
								<Td className="text-right text-zinc-600">
									{total === 0 ? "—" : `${seg.percent.toFixed(1)}%`}
								</Td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

function StackedBar(props: { counts: AttributionCounts }) {
	const segments = useMemo(
		() => buildBarSegments(props.counts),
		[props.counts],
	);
	const total = segments.reduce((sum, s) => sum + s.count, 0);

	if (total === 0) {
		return (
			<div className="rounded-[6px] border border-dashed border-zinc-200 bg-zinc-50 px-4 py-6 text-center text-sm text-zinc-500">
				No attributions recorded in this window.
			</div>
		);
	}

	return (
		<div
			className="flex flex-col gap-2"
			aria-label="Attribution distribution bar"
		>
			<div
				className="flex h-3 w-full overflow-hidden rounded-[6px] border border-zinc-200 bg-zinc-100"
				role="img"
				aria-label={`Stacked bar showing ${total} attributions across platforms.`}
			>
				{segments
					.filter((s) => s.count > 0)
					.map((seg) => (
						<div
							key={seg.platform}
							className={`${PLATFORM_COLOR[seg.platform]} h-full`}
							style={{ width: `${seg.percent}%` }}
							aria-hidden="true"
						/>
					))}
			</div>
			<div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-600">
				{segments.map((seg) => (
					<span
						key={seg.platform}
						className="inline-flex items-center gap-1.5"
					>
						<span
							aria-hidden="true"
							className={`${PLATFORM_COLOR[seg.platform]} h-2 w-2 rounded-full`}
						/>
						{seg.label}: {seg.count.toLocaleString()}
					</span>
				))}
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Shared presentational helpers
// ---------------------------------------------------------------------------

function SectionHeading(props: {
	eyebrow: string;
	title: string;
	description?: string;
}) {
	return (
		<header className="flex flex-col gap-1">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				{props.eyebrow}
			</p>
			<h2 className="text-lg font-semibold tracking-tight text-zinc-900">
				{props.title}
			</h2>
			{props.description && (
				<p className="text-sm text-zinc-600">{props.description}</p>
			)}
		</header>
	);
}

function Field(props: {
	label: string;
	htmlFor: string;
	error?: string;
	hint?: string;
	children: React.ReactNode;
}) {
	return (
		<div className="flex flex-col gap-1.5">
			<label htmlFor={props.htmlFor} className={LABEL}>
				{props.label}
			</label>
			{props.children}
			{props.hint && <p className={HINT}>{props.hint}</p>}
			{props.error && <p className={ERROR_TEXT}>{props.error}</p>}
		</div>
	);
}

function PlatformRadioGroup(props: {
	name: string;
	value: Platform;
	onChange: (platform: Platform) => void;
	error?: string;
}) {
	return (
		<fieldset className="flex flex-col gap-2">
			<legend className={LABEL}>Platform</legend>
			<div
				role="radiogroup"
				aria-label="Platform"
				className="flex flex-wrap gap-2"
			>
				{PLATFORMS.map((platform) => {
					const active = props.value === platform;
					return (
						<label
							key={platform}
							className={`inline-flex cursor-pointer items-center gap-2 rounded-[6px] border px-3 py-1.5 text-sm font-medium transition-colors ${
								active
									? "border-emerald-500 bg-emerald-50 text-emerald-700"
									: "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300"
							}`}
						>
							<input
								type="radio"
								name={props.name}
								value={platform}
								checked={active}
								onChange={() => props.onChange(platform)}
								className="sr-only"
								aria-label={PLATFORM_LABELS[platform]}
							/>
							<span
								aria-hidden="true"
								className={`h-1.5 w-1.5 rounded-full ${
									active ? "bg-emerald-500" : "bg-zinc-300"
								}`}
							/>
							{PLATFORM_LABELS[platform]}
						</label>
					);
				})}
			</div>
			{props.error && <p className={ERROR_TEXT}>{props.error}</p>}
		</fieldset>
	);
}

function PlatformChip(props: { platform: Platform }) {
	return (
		<span className="inline-flex items-center gap-2 rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-700">
			<span
				aria-hidden="true"
				className={`${PLATFORM_COLOR[props.platform]} h-1.5 w-1.5 rounded-full`}
			/>
			{PLATFORM_LABELS[props.platform]}
		</span>
	);
}

function Th(props: { children: React.ReactNode; className?: string }) {
	return (
		<th
			scope="col"
			className={`px-4 py-2.5 font-semibold ${props.className ?? ""}`}
		>
			{props.children}
		</th>
	);
}

function Td(props: { children: React.ReactNode; className?: string }) {
	return (
		<td className={`px-4 py-2.5 align-middle ${props.className ?? ""}`}>
			{props.children}
		</td>
	);
}

function EmptyState(props: { text: string }) {
	return (
		<div className="rounded-[6px] border border-dashed border-zinc-200 bg-zinc-50 px-4 py-6 text-center text-sm text-zinc-500">
			{props.text}
		</div>
	);
}

function ErrorState(props: { message: string; onRetry: () => void }) {
	return (
		<div className="flex items-center justify-between gap-3 rounded-[6px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
			<p>{props.message}</p>
			<button
				type="button"
				onClick={props.onRetry}
				className="rounded-[6px] border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
			>
				Retry
			</button>
		</div>
	);
}

function truncate(input: string, max: number): string {
	if (input.length <= max) return input;
	return `${input.slice(0, max - 1)}…`;
}
